import { FiservCardDetails, LienApiPostBody, QuickbooksCreditMemo } from '@dill/dill-shared';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { myFirebaseAuth } from '../../firebase/config';
import { BASE_API_URL } from '../../utils/constants';
import { firebaseFileUploadHelper } from '../../utils/helpers';
import { isUnconfirmedPaymentRequest, PAYMENT_UNCONFIRMED_SUBTITLE } from '../../utils/idempotency';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import { RootState } from '../store';
import { logoutUser } from './authService';

export const createExternalPaymentMethod = createAsyncThunk(
  'payments/createExternalPaymentMethod',
  async (
    {
      accountType,
      routingNumber,
      bankAccountNumber,
      userType,
      isPaymentMethodForWithdrawingFeesOnly,
      supplierId,
    }: {
      accountType: string;
      routingNumber: string;
      bankAccountNumber: string;
      userType: 'supplier' | 'buyer';
      isPaymentMethodForWithdrawingFeesOnly?: boolean;
      supplierId?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/paymentsAPI/externalBankAccount`,
        {
          accountType,
          routingNumber,
          bankAccountNumber,
          userType,
          isPaymentMethodForWithdrawingFeesOnly,
          supplierId,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const verifyDwollaMicroTransactions = createAsyncThunk(
  'payments/verifyDwollaMicroTransactions',
  async (
    {
      paymentMethodId,
      amount1,
      amount2,
    }: {
      paymentMethodId: string;
      amount1: number;
      amount2: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/generics/dwollaGenerics/verifyDwollaMicroTransactions`,
        {
          paymentMethodId,
          amount1,
          amount2,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const createPlaidPaymentMethod = createAsyncThunk(
  'payments/createPlaidPaymentMethod',
  async (
    {
      fundingSourceName,
      plaidToken,
      bankAccountType,
    }: {
      fundingSourceName: string;
      plaidToken: string;
      bankAccountType: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/paymentsAPI/plaidFundSource`,
        {
          fundingSourceName,
          plaidToken,
          bankAccountType,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierPlaidPaymentMethod = createAsyncThunk(
  'payments/createSupplierPlaidPaymentMethod',
  async (
    {
      supplierId,
      fundingSourceName,
      plaidToken,
      bankAccountType,
      isPaymentMethodForWithdrawingFeesOnly,
    }: {
      supplierId: string;
      fundingSourceName: string;
      plaidToken: string;
      bankAccountType: string;
      isPaymentMethodForWithdrawingFeesOnly?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/plaid/createSupplierPlaidFundSource`,
        {
          fundingSourceName,
          plaidToken,
          bankAccountType,
          isPaymentMethodForWithdrawingFeesOnly,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierPaymentMethod = createAsyncThunk(
  'payments/supplierBankAccount',
  async (
    {
      supplierId,
      legalBankAccountName,
    }: {
      supplierId: string;
      legalBankAccountName: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/paymentsAPI/supplierBankAccount`,
        {
          supplierId,
          legalBankAccountName,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getMainBuyerPaymentMethods = createAsyncThunk(
  'payments/getMainBuyerPaymentMethods',
  async ({ mainBuyerId }: { mainBuyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/getPaymentMethods`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierPaymentMethods = createAsyncThunk(
  'payments/getSupplierPaymentMethods',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierPaymentMethods`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const supplierWithdraw = createAsyncThunk(
  'payments/supplierWithdraw',
  async (
    {
      supplierId,
      supplierPaymentMethodId,
      amount,
    }: { supplierId: string; supplierPaymentMethodId: string; amount: number },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierWithdraw`,
        {
          supplierPaymentMethodId,
          amount,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.data) {
        return thunkApi.rejectWithValue(error.response.data);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getBuyerTransactions = createAsyncThunk(
  'payments/getBuyerTransactions',
  async (
    {
      mainBuyerId,
      startDate,
      endDate,
      timezone,
    }: { mainBuyerId: string; startDate: string; endDate: string; timezone: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/transactions`,
        {
          params: {
            startDate,
            endDate,
            timezone,
          },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      const state = thunkApi.getState() as RootState;
      if (
        response?.data?.serverRequestId &&
        state?.buyers?.selectedMainBuyer &&
        response?.data?.serverRequestId === state?.buyers?.selectedMainBuyer.id
      ) {
        return response.data;
      } else {
        console.log('========');
        return null;
      }
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getBuyerTransactionsFiles = createAsyncThunk(
  'payments/getBuyerTransactionsFiles',
  async (
    {
      mainBuyerId,
      startDate,
      endDate,
    }: { mainBuyerId: string; startDate: string; endDate: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/getTransactionsFiles?startDate=${startDate}&endDate=${endDate}`,
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dillPaymentFiles-${new Date().getTime()}.zip`);
      document.body.appendChild(link);
      link.click();
      link?.parentNode?.removeChild(link);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getTransactionById = createAsyncThunk(
  'payments/getTransactionById',
  async ({ transactionId }: { transactionId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/paymentsAPI/transactions/${transactionId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const payInvoices = createAsyncThunk(
  'payments/pay',
  async (
    {
      mainBuyerId,
      invoiceData,
      sendRelease,
      releaseData,
      idempotencyKey,
    }: {
      mainBuyerId: string;
      invoiceData: {
        senderDillPaymentMethodId: string;
        receivingSupplierId: string;
        buyerId: string;
        invoices: any;
        partialPaymentReason?: string | null;
        recordType?: 'FINANCE_CHARGE' | 'DEBIT_MEMO' | 'INVOICE';
        partialPaymentNotes?: string;
        payDateString: string;
        partialPaymentAttachmentsFiles?: File[];
        selectedCreditMemos: QuickbooksCreditMemo[];
        creditDiscountUsed?: { creditId: string; creditAmount: number }[];
        // Set when this payment was made from the Payment Plan tab's "Pay" button, so the
        // resulting transaction links back to the plan.
        paymentPlanId?: string;
      };
      sendRelease: boolean;
      releaseData: LienApiPostBody[];
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * payment so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const finalInvoiceData = invoiceData;
      const partialPaymentAttachments: { url: string; name: string }[] = [];
      if (
        invoiceData?.partialPaymentAttachmentsFiles &&
        invoiceData?.partialPaymentAttachmentsFiles.length > 0
      ) {
        for (const partialPaymentAttachmentsFile of invoiceData.partialPaymentAttachmentsFiles) {
          const blobUrl = URL.createObjectURL(partialPaymentAttachmentsFile);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${partialPaymentAttachmentsFile.name}`,
            storagePath: `transactions/${invoiceData.receivingSupplierId}/${invoiceData.buyerId}`,
          });
          if (downloadUrl) {
            partialPaymentAttachments.push({
              url: downloadUrl,
              name: partialPaymentAttachmentsFile.name,
            });
          }
        }

        delete finalInvoiceData.partialPaymentAttachmentsFiles;
      }

      const releases = [];
      for (let index = 0; index < releaseData.length; index++) {
        const release = releaseData[index];
        const downloadUrl = await firebaseFileUploadHelper({
          blobUrl: release.previewPdfUrl,
          fileNameWithExtension: `${new Date().getTime()}-unsigned.pdf`,
          storagePath: release?.notes ?? '',
        });
        releases.push({ ...release, previewPdfUrl: downloadUrl ?? '', notes: '' });
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/pay`,
        {
          invoiceData: { ...invoiceData, partialPaymentAttachments },
          sendRelease,
          releaseData: releases,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const dwollaTransactionLimitNotifyDill = createAsyncThunk(
  'payments/dwollaTransactionLimitNotifyDill',
  async (
    {
      supplierId,
      buyerId,
    }: {
      supplierId: string;
      buyerId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/paymentsAPI/dwollaTransactionLimitNotifyDill`,
        {
          supplierId,
          buyerId,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const supplierPayWillCallInvoices = createAsyncThunk(
  'payments/supplierPayWillCallInvoices',
  async (
    {
      supplierId,
      details,
      idempotencyKey,
    }: {
      supplierId: string;
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * payment so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
      details: {
        isSaveCard: boolean;
        whoPaysFee: 'SUPPLIER' | 'BUYER' | 'BOTH';
        buyerTakeRate: number;
        notes: string;
        email?: string;
        selectedDillpaymentMethodId?: string;
        paymentAmount: number;
        buyerId: string;
        receivingSupplierId: string;
        senderStripePaymentMethodId: string;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierPayWillCallInvoices`,
        {
          ...details,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const supplierFiservPayWillCallInvoices = createAsyncThunk(
  'payments/supplierFiservPayWillCallInvoices',
  async (
    {
      supplierId,
      details,
      idempotencyKey,
    }: {
      supplierId: string;
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * payment so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
      details: {
        isSaveCard: boolean;
        whoPaysFee: 'SUPPLIER' | 'BUYER' | 'BOTH';
        buyerTakeRate: number;
        notes: string;
        email?: string;
        selectedDillPaymentMethodId?: string;
        paymentAmount: number;
        buyerId: string;
        receivingSupplierId: string;
        senderFiservPaymentMethodId: string;
        // expiry: string;
        // billingName: string;
        // cardType: string;
        fiservCardDetails?: FiservCardDetails | null;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierFiservPayWillCallInvoices`,
        {
          ...details,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const getSupplierTransactions = createAsyncThunk(
  'payments/getSupplierTransactions',
  async (
    {
      supplierId,
      startDate,
      endDate,
      timezone,
      buyerId,
    }: {
      supplierId: string;
      startDate: string;
      endDate: string;
      timezone: string;
      buyerId?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const buyerIdParam = buyerId ? `&buyerId=${buyerId}` : '';
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierPayments?startDate=${startDate}&endDate=${endDate}&timezone=${timezone}${buyerIdParam}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getSupplierPosTransactions = createAsyncThunk(
  'payments/getSupplierPosTransactions',
  async (
    {
      supplierId,
      startDate,
      endDate,
      timezone,
    }: { supplierId: string; startDate: string; endDate: string; timezone: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierPayments`,
        {
          params: {
            startDate,
            endDate,
            timezone,
            transactionTypes: JSON.stringify(['WILL_CALL_PAYMENT']),
          },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getSupplierPaymentsFiles = createAsyncThunk(
  'payments/getSupplierPaymentsFiles',
  async (
    {
      supplierId,
      startDate,
      endDate,
      timezone,
      fileType,
    }: {
      supplierId: string;
      startDate: string;
      endDate: string;
      timezone: string;
      fileType?: 'invoicePayments' | 'payments' | 'all';
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/getSupplierPaymentsFiles`,
        {
          params: {
            startDate,
            endDate,
            timezone,
            fileType,
          },
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on file type
      let filename = `dillPaymentFiles-${new Date().getTime()}.zip`;
      if (fileType === 'invoicePayments') {
        filename = `dill-invoice-payments-${new Date().getTime()}.xlsx`;
      } else if (fileType === 'payments') {
        filename = `dill-payments-${new Date().getTime()}.xlsx`;
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link?.parentNode?.removeChild(link);
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getSupplierPaymentsReportJson = createAsyncThunk(
  'payments/getSupplierPaymentsReportJson',
  async (
    {
      supplierId,
      startDate,
      endDate,
      timezone,
    }: {
      supplierId: string;
      startDate: string;
      endDate: string;
      timezone: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/getSupplierPaymentsReportJson`,
        {
          params: {
            startDate,
            endDate,
            timezone,
          },
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const generateDwollaToken = createAsyncThunk(
  'payments/generateDwollaToken',
  async (
    {
      req,
      body,
    }: {
      req: any;
      body: any;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const dataReq: any = {
        action: req.action,
      };
      if (req.links) {
        dataReq._links = req.links;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/paymentsAPI/tokenUrl`,
        {
          dataReq,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getDwollaCustomer = createAsyncThunk(
  'payments/getDwollaCustomer',
  async (
    {
      email,
      id,
    }: {
      email?: string;
      id?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const params = {
        email,
        id,
      };
      const response = await axios.get<any>(`${BASE_API_URL}/paymentsAPI/getDwollaCustomer`, {
        params,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getBeneficialOwnershipStatus = createAsyncThunk(
  'payments/getBeneficialOwnershipStatus',
  async (
    {
      id,
    }: {
      id: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const params = {
        id,
      };
      const response = await axios.get<any>(
        `${BASE_API_URL}/paymentsAPI/getBeneficialOwnershipStatus`,
        {
          params,
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getWeekPaymentTransactionsLimitationsForMainBuyer = createAsyncThunk(
  'payments/getWeekPaymentTransactionsLimitationsForMainBuyer',
  async ({ mainBuyerId }: { mainBuyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/weekPaymentTransactionsLimitations`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getStripeConfig = createAsyncThunk(
  'payments/getStripeConfig',
  async (data, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(`${BASE_API_URL}/generics/stripeGenerics/getConfig`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const createStripePaymentMethod = createAsyncThunk(
  'payments/createStripePaymentMethod',
  async (
    {
      paymentMethodId,
      billingName,
      mainBuyerId,
    }: { mainBuyerId: string; billingName: string; paymentMethodId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/addStripePaymentMethod`,
        {
          paymentMethodId,
          billingName,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierCustomerStripePaymentMethod = createAsyncThunk(
  'payments/createSupplierCustomerStripePaymentMethod',
  async (
    {
      supplierId,
      paymentMethodId,
      billingName,
      buyerId,
      customerEmail,
    }: {
      supplierId: string;
      buyerId: string;
      billingName: string;
      paymentMethodId: string;
      customerEmail: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/createSupplierCustomerPaymentMethod`,
        {
          buyerId,
          paymentMethodId,
          billingName,
          customerEmail,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierCustomerFiservPaymentMethod = createAsyncThunk(
  'payments/createSupplierCustomerFiservPaymentMethod',
  async (
    {
      supplierId,
      buyerId,
      customerEmail,
      fiservCardDetails,
    }: {
      supplierId: string;
      buyerId: string;
      fiservCardDetails: FiservCardDetails;
      customerEmail: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/createSupplierCustomerFiservPaymentMethod`,
        {
          buyerId,
          // paymentMethodId,
          // billingName,
          customerEmail,
          fiservCardDetails,
          // expiry,
          // cardType,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const getStripeSupplierAccount = createAsyncThunk(
  'payments/getStripeSupplierAccount',
  async (data, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(`${BASE_API_URL}/paymentsAPI/stripe/supplierAccount`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const createStripeExternalBankToSupplierAccount = createAsyncThunk(
  'payments/createStripeExternalBankToSupplierAccount',
  async ({ bankOrCardToken }: { bankOrCardToken: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/paymentsAPI/stripe/supplierAccount/externalBank`,
        { bankOrCardToken },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const getSupplierRegistrationStatus = createAsyncThunk(
  'payments/getSupplierRegistrationStatus',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/supplierRegistrationStatus`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateTransaction = createAsyncThunk(
  'payments/updateTransaction',
  async (
    {
      mainBuyerId,
      transactionId,
      transactionDetails,
    }: {
      mainBuyerId: string;
      transactionId: string;
      transactionDetails: any;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        BASE_API_URL + `/mainBuyers/${mainBuyerId}/transactions/${transactionId}`,
        { ...transactionDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
export const deletePaymentMethodByMainBuyer = createAsyncThunk(
  'payments/deletePaymentMethod',
  async (
    {
      mainBuyerId,
      paymentMethodId,
    }: {
      mainBuyerId: string;
      paymentMethodId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        BASE_API_URL + `/mainBuyers/${mainBuyerId}/payments/deletePaymentMethod/${paymentMethodId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const sendInvoicePaymentReminderEmail = createAsyncThunk(
  'payments/sendInvoicePaymentReminderEmail',
  async (
    {
      supplierId,
      buyerId,
      invoiceId,
      subjectLine,
      notes,
      attachments,
      reminderType,
      excludedUserIds,
      additionalEmails,
      includeInvoicePdf,
      mergeInvoicePdf,
    }: {
      supplierId: string;
      buyerId: string;
      invoiceId: string;
      subjectLine?: string;
      notes?: string;
      attachments?: File[];
      reminderType?: 'PAYMENT' | 'CREDIT_HOLD';
      excludedUserIds?: string[];
      additionalEmails?: string[];
      includeInvoicePdf?: boolean;
      mergeInvoicePdf?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload attachments to Firebase if provided
      const uploadedAttachments: { url: string; name: string }[] = [];
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          const blobUrl = URL.createObjectURL(attachment);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${attachment.name}`,
            storagePath: `payment-reminders/${supplierId}/${buyerId}`,
          });
          if (downloadUrl) {
            uploadedAttachments.push({
              url: downloadUrl,
              name: attachment.name,
            });
          }
        }
      }

      // Send POST request for all reminders (automatic or customized)
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/sendInvoicePaymentReminderEmail`,
        {
          buyerId,
          invoiceId,
          subjectLine,
          notes,
          attachments: uploadedAttachments,
          reminderType,
          excludedUserIds,
          additionalEmails,
          includeInvoicePdf,
          mergeInvoicePdf,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const sendBulkInvoicePaymentReminderEmails = createAsyncThunk(
  'payments/sendBulkInvoicePaymentReminderEmails',
  async (
    {
      supplierId,
      buyerId,
      invoiceIds,
      subjectLine,
      notes,
      attachments,
      reminderType,
      excludedUserIds,
      additionalEmails,
      includeInvoicePdf,
      mergeInvoicePdf,
    }: {
      supplierId: string;
      buyerId: string;
      invoiceIds: string[];
      subjectLine?: string;
      notes?: string;
      attachments?: File[];
      reminderType?: 'PAYMENT' | 'CREDIT_HOLD';
      excludedUserIds?: string[];
      additionalEmails?: string[];
      includeInvoicePdf?: boolean;
      mergeInvoicePdf?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      // Upload attachments to Firebase if provided
      const uploadedAttachments: { url: string; name: string }[] = [];
      if (attachments && attachments.length > 0) {
        for (const attachment of attachments) {
          const blobUrl = URL.createObjectURL(attachment);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${attachment.name}`,
            storagePath: `payment-reminders/${supplierId}/${buyerId}`,
          });
          if (downloadUrl) {
            uploadedAttachments.push({
              url: downloadUrl,
              name: attachment.name,
            });
          }
        }
      }

      // Send POST request for bulk reminder
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/sendBulkInvoicePaymentReminderEmails`,
        {
          buyerId,
          invoiceIds,
          subjectLine,
          notes,
          attachments: uploadedAttachments,
          reminderType,
          excludedUserIds,
          additionalEmails,
          includeInvoicePdf,
          mergeInvoicePdf,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getSupplierCustomerPaymentMethods = createAsyncThunk(
  'payments/getSupplierCustomerPaymentMethods',
  async (
    {
      supplierId,
      customerId,
    }: { supplierId: string; customerId: string; isHideLoadingIndicator?: boolean },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/getSupplierCustomerPaymentMethods/${customerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const deleteSupplierCustomerPaymentMethod = createAsyncThunk(
  'payments/deleteSupplierCustomerPaymentMethod',
  async (
    {
      supplierId,
      customerId,
      paymentMethodId,
    }: { supplierId: string; customerId: string; paymentMethodId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/deleteSupplierCustomerPaymentMethod/${paymentMethodId}?customerId=${customerId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const supplierChargeInvoices = createAsyncThunk(
  'payments/supplierChargeInvoices',
  async (
    {
      supplierId,
      buyerId,
      whoPaysFee,
      buyerTakeRate,
      invoiceData,
      idempotencyKey,
    }: {
      supplierId: string;
      buyerId: string;
      whoPaysFee: 'SUPPLIER' | 'BUYER' | 'BOTH';
      buyerTakeRate: number;
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * charge so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
      invoiceData: {
        senderDillPaymentMethodId: string;
        receivingSupplierId: string;
        buyerId: string;
        invoices: any;
        partialPaymentReason?: string | null;
        recordType?: 'FINANCE_CHARGE' | 'DEBIT_MEMO' | 'INVOICE';
        partialPaymentNotes?: string;
        payDateString: string;
        partialPaymentAttachmentsFiles?: File[];
        selectedCreditMemos: QuickbooksCreditMemo[];
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const finalInvoiceData = invoiceData;
      const partialPaymentAttachments: { url: string; name: string }[] = [];
      if (
        invoiceData?.partialPaymentAttachmentsFiles &&
        invoiceData?.partialPaymentAttachmentsFiles.length > 0
      ) {
        for (const partialPaymentAttachmentsFile of invoiceData.partialPaymentAttachmentsFiles) {
          const blobUrl = URL.createObjectURL(partialPaymentAttachmentsFile);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl: blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${partialPaymentAttachmentsFile.name}`,
            storagePath: `transactions/${invoiceData.receivingSupplierId}/${invoiceData.buyerId}`,
          });
          if (downloadUrl) {
            partialPaymentAttachments.push({
              url: downloadUrl,
              name: partialPaymentAttachmentsFile.name,
            });
          }
        }

        delete finalInvoiceData.partialPaymentAttachmentsFiles;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/charge`,
        {
          invoiceData: { ...invoiceData, partialPaymentAttachments },
          buyerId,
          whoPaysFee,
          buyerTakeRate,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getPlaidBankBalance = createAsyncThunk(
  'payments/getPlaidBankBalance',
  async (
    {
      accessToken,
    }: {
      accessToken: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/paymentsAPI/plaid/getPlaidBankBalance`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            accessToken,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getPlaidBankStatementsPDF = createAsyncThunk(
  'payments/getPlaidBankStatementsPDF',
  async (
    {
      accessToken,
      date,
      monthsRange,
    }: {
      accessToken: string;
      date: string;
      monthsRange: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/paymentsAPI/plaid/getPlaidBankStatementPDF`,
        {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            accessToken,
            date,
            monthsRange,
          },
        }
      );

      const file = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(file);
      link.download = `statement-bank-${+new Date()}.pdf`;
      link.click();

      return { success: true, data: { pdfUrl: link.href } };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = 'Error getting bank statements';

      // Handle blob error response
      if (
        error?.response?.data instanceof Blob &&
        error?.response?.data.type === 'application/json'
      ) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          if (json?.errors) {
            errorMessage = json.errors.map((e: any) => e.msg).join(' ');
          } else if (json?.message) {
            errorMessage = json.message;
          }
        } catch (parseError) {
          errorMessage = 'Failed getting bank statements';
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      console.error('Parsed error message:', errorMessage);
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getPlaidBankStatements = createAsyncThunk(
  'payments/getPlaidBankStatements',
  async (
    {
      accessToken,
      date,
      monthsRange,
    }: {
      accessToken: string[];
      date: string;
      monthsRange: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/paymentsAPI/plaid/getPlaidBankStatement`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          params: {
            accessToken,
            date,
            monthsRange,
          },
        }
      );

      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = 'Error getting bank statements';

      if (error?.message) {
        errorMessage = error.message;
      }

      console.error('Parsed error message:', errorMessage);
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const resendInvoicePdfReceipt = createAsyncThunk(
  'payments/resendInvoicePdfReceipt',
  async (
    {
      supplierId,
      transactionId,
      email,
    }: { supplierId: string; transactionId: string; email: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/resendReceiptPdf`,
        {
          transactionId,
          email,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const refundFiservTransaction = createAsyncThunk(
  'payments/refundFiservTransaction',
  async (
    {
      supplierId,
      transactionId,
      amountToRefund,
    }: { supplierId: string; transactionId: string; amountToRefund: number },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/refundFiservTransaction`,
        { amountToRefund, transactionId },

        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const payFixedAmount = createAsyncThunk(
  'payments/payFixedAmount',
  async (
    {
      mainBuyerId,
      paymentData,
      idempotencyKey,
    }: {
      mainBuyerId: string;
      paymentData: {
        senderDillPaymentMethodId: string;
        receivingSupplierId: string;
        buyerId: string;
        paymentAmount: number;
        remittanceNotes: string;
        payDateString: string;
      };
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * payment so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      console.log({ paymentData });
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/payFixedAmount`,
        {
          paymentData: { ...paymentData },
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createFiservPaymentMethod = createAsyncThunk(
  'payments/createFiservPaymentMethod',
  async (
    {
      mainBuyerId,
      fiservCardDetails,
    }: {
      mainBuyerId: string;
      fiservCardDetails: FiservCardDetails;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/addFiservPaymentMethod`,
        {
          fiservCardDetails,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getFiservCardDetails = createAsyncThunk(
  'payments/getFiservCardDetails',
  async ({ fiservCardToken }: { fiservCardToken: string }, thunkApi) => {
    try {
      const response = await axios.get<any>(
        `${BASE_API_URL}/generics/fiservGenerics/getFiservCardDetails/${fiservCardToken}`
      );
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, data: {} };
      }
    } catch (error: any) {
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

/**
 * Queues a future-dated payment. Same payload shape as `payInvoices` plus `scheduledDate`, so the
 * backend can reuse the immediate-payment parsing and the client can reuse buildInvoicePayments.
 */
export const createScheduledPayment = createAsyncThunk(
  'payments/createScheduledPayment',
  async (
    {
      mainBuyerId,
      invoiceData,
      scheduledDate,
      idempotencyKey,
    }: {
      mainBuyerId: string;
      invoiceData: {
        senderDillPaymentMethodId: string;
        receivingSupplierId: string;
        buyerId: string;
        invoices: any;
        selectedCreditMemos: QuickbooksCreditMemo[];
        creditDiscountUsed?: { creditId: string; creditAmount: number }[];
        totalCreditsToUse?: number;
        // Both optional because this is the same body `payInvoices` receives, and the caller sends it
        // unchanged. The backend derives the total it quotes the buyer from the invoice snapshot
        // rather than trusting a figure from here, so omitting it is the normal case.
        totalAmount?: number;
        payDateString?: string;
        partialPaymentReason?: string | null;
        partialPaymentNotes?: string;
        partialPaymentAttachmentsFiles?: File[];
      };
      /** ISO date string for the day the payment should run. */
      scheduledDate: string;
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const finalInvoiceData: any = { ...invoiceData };
      const partialPaymentAttachments: { url: string; name: string }[] = [];
      if (
        invoiceData?.partialPaymentAttachmentsFiles &&
        invoiceData?.partialPaymentAttachmentsFiles.length > 0
      ) {
        for (const partialPaymentAttachmentsFile of invoiceData.partialPaymentAttachmentsFiles) {
          const blobUrl = URL.createObjectURL(partialPaymentAttachmentsFile);
          const downloadUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-${partialPaymentAttachmentsFile.name}`,
            storagePath: `transactions/${invoiceData.receivingSupplierId}/${invoiceData.buyerId}`,
          });
          if (downloadUrl) {
            partialPaymentAttachments.push({
              url: downloadUrl,
              name: partialPaymentAttachmentsFile.name,
            });
          }
        }
        delete finalInvoiceData.partialPaymentAttachmentsFiles;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/scheduledPayments`,
        {
          invoiceData: { ...finalInvoiceData, partialPaymentAttachments },
          scheduledDate,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
            ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
          },
        }
      );
      return {
        success: true,
        data: response.data?.data,
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      if (isUnconfirmedPaymentRequest(error)) {
        return thunkApi.rejectWithValue(PAYMENT_UNCONFIRMED_SUBTITLE);
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getScheduledPayments = createAsyncThunk(
  'payments/getScheduledPayments',
  async (
    {
      mainBuyerId,
      supplierId,
      buyerId,
    }: { mainBuyerId: string; supplierId?: string; buyerId?: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/scheduledPayments`,
        {
          params: { ...(supplierId ? { supplierId } : {}), ...(buyerId ? { buyerId } : {}) },
          headers: { Authorization: 'Bearer ' + token },
        }
      );
      return { success: true, data: response.data?.data ?? [] };
    } catch (error: any) {
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

/**
 * Moves a scheduled payment to a new date.
 *
 * A date change can change what the buyer pays — early-pay discounts and card-fee waivers are
 * priced against the day the money moves. The backend answers 409 in that case instead of applying
 * the move, and this resolves (rather than rejects) with the two totals so the caller can ask the
 * buyer and retry with `confirmedTotalAmount`. It is a question, not a failure.
 */
export const updateScheduledPaymentDate = createAsyncThunk(
  'payments/updateScheduledPaymentDate',
  async (
    {
      mainBuyerId,
      scheduledPaymentId,
      scheduledDate,
      confirmedTotalAmount,
    }: {
      mainBuyerId: string;
      scheduledPaymentId: string;
      scheduledDate: string;
      confirmedTotalAmount?: number;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.patch<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/scheduledPayments/${scheduledPaymentId}`,
        {
          scheduledDate,
          ...(confirmedTotalAmount != null ? { confirmedTotalAmount } : {}),
        },
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return { success: true, data: response.data?.data };
    } catch (error: any) {
      const confirmation = error?.response?.data?.data;
      if (error?.response?.status === 409 && confirmation?.requiresTotalConfirmation) {
        return {
          success: false,
          requiresTotalConfirmation: true,
          previousTotalAmount: Number(confirmation.previousTotalAmount ?? 0),
          newTotalAmount: Number(confirmation.newTotalAmount ?? 0),
          message: error?.response?.data?.errors?.[0]?.msg ?? '',
        };
      }
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const cancelScheduledPayment = createAsyncThunk(
  'payments/cancelScheduledPayment',
  async (
    { mainBuyerId, scheduledPaymentId }: { mainBuyerId: string; scheduledPaymentId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      await axios.delete<any>(
        `${BASE_API_URL}/mainBuyers/${mainBuyerId}/payments/scheduledPayments/${scheduledPaymentId}`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return { success: true, scheduledPaymentId };
    } catch (error: any) {
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

/**
 * Scheduled payments buyers have queued to this supplier. Read-only: the buyer owns the schedule, so
 * there is no supplier-side reschedule or cancel.
 */
export const getSupplierScheduledPayments = createAsyncThunk(
  'payments/getSupplierScheduledPayments',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/payments/scheduledPayments`,
        { headers: { Authorization: 'Bearer ' + token } }
      );
      return { success: true, data: response.data?.data ?? [] };
    } catch (error: any) {
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
